import apiFetch from "@wordpress/api-fetch";
import { useDispatch, useSelect } from "@wordpress/data";
import { __ } from "@wordpress/i18n";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import Button from "../../button";
import { transition } from "../auth-state-machine";
import { authStore } from "../auth-store";
import { applyNewNonce, handleApiErrorOrReload } from "../auth-utils";
import { defaultSubmitTextForStage } from "../default-copy";

type Props = { copySubmitOverride?: string };

const lostPasswordUrl = (): string => {
	const myAccount =
		(typeof window !== "undefined" &&
			window.leat?.storePages?.myaccount?.permalink) ||
		"/my-account/";
	const here = typeof window !== "undefined" ? window.location.href : "/";
	return `${myAccount.replace(/\/$/, "")}/lost-password/?redirect_to=${encodeURIComponent(here)}`;
};

export const PasswordStage: React.FC<Props> = ({ copySubmitOverride }) => {
	const email = useSelect((select) => select(authStore).getEmail(), []);
	const intent = useSelect((select) => select(authStore).getIntent(), []);
	const error = useSelect((select) => select(authStore).getError(), []);
	const isSubmitting = useSelect(
		(select) => select(authStore).isSubmitting(),
		[],
	);
	const { setError, setSubmitting, setStage, setPrefillNames } =
		useDispatch(authStore);

	const [password, setPassword] = useState("");
	const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
	const [now, setNow] = useState(Date.now());
	const abortRef = useRef<AbortController | null>(null);

	// Abort any pending fetch when the stage unmounts (modal close, stage change).
	useEffect(() => {
		return () => {
			abortRef.current?.abort();
		};
	}, []);

	// Tick the 60s countdown when rate-limited, then stop and clear so the
	// interval doesn't keep firing once the lockout has elapsed.
	useEffect(() => {
		if (!rateLimitedUntil) return;
		const t = setInterval(() => {
			const current = Date.now();
			setNow(current);
			if (current >= rateLimitedUntil) {
				setRateLimitedUntil(null);
			}
		}, 500);
		return () => clearInterval(t);
	}, [rateLimitedUntil]);

	const remainingSec = rateLimitedUntil
		? Math.max(0, Math.ceil((rateLimitedUntil - now) / 1000))
		: 0;
	const isDisabled = isSubmitting || !password || remainingSec > 0;

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!password) return;
		setError(null);
		setSubmitting(true);
		abortRef.current?.abort();
		const ac = new AbortController();
		abortRef.current = ac;
		try {
			const res = await apiFetch<{
				success: true;
				userId: number;
				isEnrolled: boolean;
				firstName: string;
				lastName: string;
				nonce: string;
			}>({
				path: "/leat/v1/auth/login",
				method: "POST",
				data: { email, password },
				signal: ac.signal,
			});
			applyNewNonce(res.nonce);
			setPassword("");
			const next = transition(
				{ stage: "password", email },
				{
					type: "loginSuccess",
					isEnrolled: res.isEnrolled,
					firstName: res.firstName,
					lastName: res.lastName,
				},
				intent,
			);
			if (
				next.prefillFirstName !== undefined &&
				next.prefillLastName !== undefined
			) {
				setPrefillNames(next.prefillFirstName, next.prefillLastName);
			}
			setStage(next.stage);
		} catch (err) {
			if ((err as { name?: string })?.name === "AbortError") return;
			const apiErr = err as {
				code?: string;
				data?: { scope?: "account" | "ip" };
			};
			if (
				apiErr?.code === "too_many_attempts" &&
				apiErr.data?.scope !== "account"
			) {
				setRateLimitedUntil(Date.now() + 60_000);
			}
			const msg = handleApiErrorOrReload(err);
			if (msg) setError(msg);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form onSubmit={onSubmit} noValidate>
			<div className="leat-auth-modal__field">
				<label htmlFor="leat-auth-email-readonly">
					{__("Email", "leat-crm")}
				</label>
				<input
					id="leat-auth-email-readonly"
					type="email"
					value={email}
					readOnly
					aria-readonly="true"
				/>
				<button
					type="button"
					className="leat-auth-modal__back-link"
					onClick={() => setStage("emailEntry")}
				>
					{__("Use a different email", "leat-crm")}
				</button>
			</div>
			<div className="leat-auth-modal__field">
				<label htmlFor="leat-auth-password">{__("Password", "leat-crm")}</label>
				<input
					id="leat-auth-password"
					type="password"
					autoComplete="current-password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					required
				/>
				<a className="leat-auth-modal__forgot" href={lostPasswordUrl()}>
					{__("Forgot password?", "leat-crm")}
				</a>
			</div>
			{error && (
				<div className="leat-auth-modal__submit-error" role="alert">
					{error}
				</div>
			)}
			{remainingSec > 0 && (
				<span aria-live="polite" className="leat-auth-modal__countdown">
					{remainingSec}s
				</span>
			)}
			<Button
				type="submit"
				variant="contained"
				className="leat-auth-modal__submit leat-button--pill"
				disabled={isDisabled}
			>
				{isSubmitting
					? __("Signing in…", "leat-crm")
					: (copySubmitOverride ?? defaultSubmitTextForStage("password"))}
			</Button>
		</form>
	);
};
